import React, { useState, useEffect } from 'react';
import { LogIn, LogOut, CheckCircle, AlertCircle, Mail, X, User } from 'lucide-react';
import { API_BASE_URL } from '../config';
import './EnterpriseDMS.css';

// ── JWT parser ────────────────────────────────────────────────────────────────
function parseJwt(token) {
    if (!token || typeof token !== 'string') return null;
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4 !== 0) base64 += '=';
        const json = decodeURIComponent(
            atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
        );
        return JSON.parse(json);
    } catch (e) {
        return null;
    }
}

function readUserFromStorage() {
    const token = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
    return parseJwt(token);
}

// ── Component ─────────────────────────────────────────────────────────────────
function ProfilePage({ onBack, onOpenSettings }) {
    const [user, setUser] = useState(() => readUserFromStorage());
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        const refresh = () => { setUser(readUserFromStorage()); setImgError(false); };
        window.addEventListener('dms-auth-updated', refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener('dms-auth-updated', refresh);
            window.removeEventListener('storage', refresh);
        };
    }, []);

    const isLoggedIn = !!user;
    const name = user?.name || '';
    const email = user?.email || '';
    const displayName = name || (email ? email.split('@')[0] : 'User');
    const provider = user?.provider || '';

    // Profile photo resolution order:
    // 1. picture (Google OAuth sets this)
    // 2. avatar (GitHub OAuth sets this)
    // 3. ui-avatars.com initials (final fallback — always works)
    let photoUrl = null;
    if (!imgError) {
        if (user?.picture) {
            photoUrl = user.picture;
        } else if (user?.avatar) {
            photoUrl = user.avatar;
        }
    }

    const resolvePhotoUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
        const path = url.startsWith('/') ? url : '/' + url;
        return `${base}${path}`;
    };


    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleLogout = () => {
        localStorage.removeItem('dms_auth_token');
        sessionStorage.removeItem('dms_auth_token');
        window.dispatchEvent(new Event('dms-auth-updated'));
        window.location.replace(window.location.origin + '/');
    };

    const handleLogin = () => {
        if (onBack) onBack();
        window.location.replace(window.location.origin + '/');
    };

    return (
        <div className="profile-drawer premium-profile-modal" style={{
            position: 'absolute', top: '70px', right: '30px',
            width: '380px',
            background: 'var(--card-bg, #ffffff)',
            backdropFilter: 'blur(12px)',
            borderRadius: '22px',
            boxShadow: '0 18px 40px rgba(15,23,42,0.22), 0 0 0 1px rgba(59,130,246,0.18)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden', zIndex: 1000,
            animation: 'fadeInScale 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards',
            transformOrigin: 'top right'
        }}>
            <style>
                {`
                @keyframes fadeInScale {
                    from { opacity: 0; transform: scale(0.98); }
                    to { opacity: 1; transform: scale(1); }
                }
                .premium-profile-modal .close-btn {
                    background: #ffffff;
                    color: #2563eb;
                }
                .premium-profile-modal .close-btn:hover {
                    background: #eff6ff;
                    color: #1d4ed8;
                    transform: scale(1.05);
                }
                .premium-profile-modal .avatar-container {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                .premium-profile-modal .avatar-container:hover {
                    transform: scale(1.05);
                    box-shadow: 0 0 25px rgba(37,99,235,0.6), 0 6px 20px rgba(0,0,0,0.15);
                }
                .premium-profile-modal .action-btn {
                    transition: all 0.2s ease;
                }
                .premium-profile-modal .action-btn:hover {
                    transform: translateY(-2px);
                }
                .premium-profile-modal .edit-btn:hover {
                    background: #1d4ed8 !important;
                    box-shadow: 0 6px 16px rgba(37, 99, 235, 0.4) !important;
                }
                .premium-profile-modal .signout-btn:hover {
                    background: #dc2626 !important;
                    box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4) !important;
                }
                `}
            </style>

            {/* ── Banner (Edge to Edge) ───────────────────────── */}
            <div style={{
                height: '140px',
                background: 'linear-gradient(135deg, #0B1F3A 0%, #112D5A 100%)',
                position: 'relative', overflow: 'hidden',
            }}>
                <button className="close-btn" onClick={onBack} style={{ 
                    position: 'absolute', top: '16px', right: '16px', zIndex: 10,
                    border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '50%',
                    transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                }}>
                    <X size={18} />
                </button>
                {/* Subtle mesh pattern */}
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px)',
                    backgroundSize: '20px 20px', opacity: 0.8
                }} />
                {/* Soft blue glow */}
                <div style={{ position: 'absolute', top: -40, right: 20, width: 140, height: 140, borderRadius: '50%', background: 'rgba(37, 99, 235, 0.3)', filter: 'blur(35px)' }} />
            </div>

            {/* Profile body */}
            <div style={{ padding: '0 2rem 2.5rem 2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                
                {/* Avatar */}
                <div style={{ marginTop: '-50px', marginBottom: '1.25rem', position: 'relative' }}>
                    <div className="avatar-container" style={{
                        width: '100px', height: '100px', borderRadius: '50%',
                        border: '4px solid #ffffff',
                        boxShadow: '0 0 20px rgba(37,99,235,0.4), 0 4px 15px rgba(0,0,0,0.1)',
                        overflow: 'hidden', flexShrink: 0,
                        background: 'linear-gradient(135deg, #0B1F3A, #2563EB)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        {photoUrl ? (
                            <img
                                src={resolvePhotoUrl(photoUrl)}
                                alt="Profile"
                                referrerPolicy="no-referrer"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={() => setImgError(true)}
                            />
                        ) : isLoggedIn ? (
                            <img
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=1e3a8a&color=fff&size=220&font-size=0.4&bold=true`}
                                alt="Avatar"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <User size={35} style={{ color: '#fff', opacity: 0.9 }} />
                        )}
                    </div>
                </div>

                {/* Name and Role */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.5rem', marginTop: '0.5rem' }}>
                    <h2 style={{ margin: 0, fontSize: '28px', fontWeight: 800, color: 'var(--text-main)', letterSpacing: '-0.5px' }}>{isLoggedIn ? displayName : 'Guest'}</h2>
                    {isLoggedIn && (
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px rgba(16,185,129,0.8)' }} title="Active Session" />
                    )}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: '15px', fontWeight: 500 }}>
                    {user?.email || 'Please sign in to continue.'}
                </div>

                {/* Separator */}
                <hr style={{ width: '100%', border: 'none', borderTop: '1px solid rgba(59,130,246,0.1)', margin: '1.75rem 0' }} />

                {/* Actions */}
                <div style={{ display: 'flex', width: '100%', gap: '16px', justifyContent: 'center' }}>
                    {isLoggedIn ? (
                        <>
                            <button
                                className="action-btn signout-btn"
                                onClick={handleLogout}
                                style={{
                                    background: '#ef4444',
                                    border: 'none',
                                    borderRadius: '12px', color: '#fff',
                                    cursor: 'pointer', padding: '14px 16px',
                                    fontSize: '0.95rem', fontWeight: 700,
                                    flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: '8px',
                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)'
                                }}
                            >
                                <LogOut size={18} color="#fff" /> Sign Out
                            </button>
                            <button
                                className="action-btn edit-btn"
                                onClick={() => { if (onOpenSettings) onOpenSettings(); }}
                                style={{
                                    background: '#2563eb',
                                    border: 'none',
                                    borderRadius: '12px', color: '#fff',
                                    cursor: 'pointer', padding: '14px 16px',
                                    fontSize: '0.95rem', fontWeight: 700,
                                    flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: '8px',
                                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)'
                                }}
                            >
                                <User size={18} color="#fff" /> Edit
                            </button>
                        </>
                    ) : (
                        <button
                            className="action-btn edit-btn"
                            onClick={handleLogin}
                            style={{
                                background: '#2563eb',
                                border: 'none', borderRadius: '12px', color: '#fff',
                                cursor: 'pointer', padding: '12px 24px',
                                fontSize: '0.95rem', fontWeight: 700,
                                display: 'flex', alignItems: 'center', gap: '8px',
                                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                                flex: 1, justifyContent: 'center'
                            }}
                        >
                            <LogIn size={18} color="#fff" /> Log In
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProfilePage;
